/**
 * Cache de edge (Cache API do Cloudflare, compartilhada entre isolates do
 * Worker no mesmo datacenter — sobrevive a reciclagem de instância,
 * diferente do cache em memória `Map` que o próprio framework já tem).
 * Não existe no `vite dev` (Node) nem em testes, só no runtime real do
 * Cloudflare Workers — nesses casos vira passthrough (chama `compute()`
 * sempre, sem cachear).
 *
 * Qualquer falha de leitura/escrita de cache cai de volta pra `compute()` —
 * cache é otimização, nunca pode ser o motivo de uma página não mostrar
 * conteúdo (ver PLANO-REDESIGN.md, item 19, bug do loader não registrado
 * mascarado como "bug de cache").
 */
// Coalesce chamadas concorrentes com a mesma (cacheName, cacheKeyUrl): sem
// isso, duas seções pedindo os mesmos dados quase ao mesmo tempo (ex:
// SeoPLP e a grade de produtos, ambas chamando productListingPageLean)
// disparam duas requisições reais à VTEX, porque nenhuma acha a entrada de
// cache da outra a tempo — o `cache.put` da primeira só acontece depois
// que a segunda já começou seu próprio `compute()`. Escopo: por isolate do
// Worker (top-level do módulo) — não precisa sobreviver entre requisições
// nem ser compartilhado entre isolates, só cobrir a janela de corrida
// dentro de uma única renderização de página.
const inFlight = new Map<string, Promise<unknown>>();

export async function withEdgeCache<T>(
  cacheName: string,
  cacheKeyUrl: string,
  ttlSeconds: number,
  compute: () => Promise<T>,
): Promise<T> {
  const inFlightKey = `${cacheName}:${cacheKeyUrl}`;
  const existing = inFlight.get(inFlightKey);
  if (existing) return existing as Promise<T>;

  const promise = withEdgeCacheUncoalesced(cacheName, cacheKeyUrl, ttlSeconds, compute);
  inFlight.set(inFlightKey, promise);
  try {
    return await promise;
  } finally {
    inFlight.delete(inFlightKey);
  }
}

async function withEdgeCacheUncoalesced<T>(
  cacheName: string,
  cacheKeyUrl: string,
  ttlSeconds: number,
  compute: () => Promise<T>,
): Promise<T> {
  // `import.meta.env.DEV`, não só `typeof caches`: no `vite dev` local (plugin
  // @cloudflare/vite-plugin), `caches` existe (emulado via workerd), mas
  // `cache.match`/`cache.put` fazem um round-trip HTTP interno que reentra no
  // mesmo processo Node que já está servindo a requisição atual — trava em
  // deadlock e derruba o servidor inteiro com um "fetch failed" não
  // capturável (acontece fora do try/catch abaixo, na própria emulação do
  // plugin). Só existe de verdade no runtime real do Cloudflare Workers.
  const cache = typeof caches === "undefined" || import.meta.env.DEV
    ? null
    : await caches.open(cacheName).catch(() => null);
  const cacheKey = cache ? new Request(cacheKeyUrl) : null;

  if (cache && cacheKey) {
    try {
      const cached = await cache.match(cacheKey);
      if (cached) {
        return (await cached.json()) as T;
      }
    } catch (err) {
      console.error(`[edgeCache:${cacheName}] leitura falhou:`, err);
    }
  }

  const result = await compute();

  if (cache && cacheKey && result != null) {
    try {
      await cache.put(
        cacheKey,
        new Response(JSON.stringify(result), {
          headers: {
            "content-type": "application/json",
            "cache-control": `max-age=${ttlSeconds}`,
          },
        }),
      );
    } catch (err) {
      console.error(`[edgeCache:${cacheName}] escrita falhou:`, err);
    }
  }

  return result;
}

export function cacheKeyForRequest(request: Request | undefined, fallback: string): string {
  const identity = request?.url || fallback;
  return `https://edge-cache.internal/${encodeURIComponent(identity)}`;
}
